import React from 'react';
import { Box, useTheme } from '@mui/material';
import Grid from '@mui/material/Grid2';

import { ReactComponent as HandymanIcon } from '../../assets/icons/info/role/handyman.svg';
import { ReactComponent as CustomerIcon } from '../../assets/icons/info/role/customer.svg';

import { UserRoleType } from './SignUpFormWizzard';
import {
  GlassCategoryCard,
  GlassCategoryIcon,
  GlassCategoryTitle,
  GlassCategoryDescription,
  SectionHeader,
  GlassColors,
  BodyText,
  ButtonTertiary,
} from '../glassmorphism/GlassmorphismStyles';
import { useNavigate } from 'react-router-dom';
import RailStepper from '../../components/RailStepper/RailStepper';

interface PassedProps {
  onSelect: (role: UserRoleType) => void;
  isHandyman: boolean;
}

const ChooseRoleStep = (props: PassedProps) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        maxWidth: '760px',
        mx: 'auto',
        gap: { xs: '16px', sm: '24px' },
      }}
    >
      <Box
        sx={{
          textAlign: 'center',
          maxWidth: { xs: '208px', sm: 'none' },
          margin: '0 auto',
        }}
      >
        <SectionHeader
          variant='h4'
          sx={{
            fontSize: { xs: '20px', sm: '24px' },
            lineHeight: { xs: '160%', sm: '133%' },
            letterSpacing: { xs: '.15px', sm: 0 },
            fontFamily: 'Montserrat, sans-serif',
            fontWeight: { xs: 800, sm: 700 },
            filter: 'drop-shadow(0px -0.25px 0.4px #0000008C)',
            color: isDark
              ? GlassColors.dark.accent
              : GlassColors.light.secondary,
            mb: '8px',
          }}
        >
          Find help or start earning
        </SectionHeader>
        <BodyText
          sx={{
            fontSize: { xs: '14px', sm: '16px' },
            fontWeight: 700,
            lineHeight: { xs: '157%', sm: '175%' },
            letterSpacing: { xs: '.1px', sm: '.15px' },
            maxWidth: '304px',
            mx: 'auto',
          }}
        >
          Choose how you'd like to start
        </BodyText>
      </Box>

      <Grid container spacing={{ xs: 2, sm: 3 }}>
        <Grid size={{ xs: 6, sm: 6 }}>
          <GlassCategoryCard
            onClick={() => props.onSelect('customer')}
            sx={{
              cursor: 'pointer',
              display: 'flex',
              height: '100%',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'start',
              gap: 2,
              transition: 'box-shadow 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              padding: { xs: '22px 12px', sm: '60px 32px' },
              borderRadius: { xs: '32px', sm: '64px' },
            }}
          >
            <GlassCategoryIcon
              sx={{
                width: '128px',
                height: '128px',
              }}
            >
              <CustomerIcon />
            </GlassCategoryIcon>
            <Box sx={{ textAlign: 'center' }}>
              <GlassCategoryTitle
                variant='h6'
                sx={{
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: { xs: '16px', sm: '24px' },
                  lineHeight: { xs: '175%', sm: '133%' },
                  letterSpacing: { xs: '.15px', sm: 0 },
                  fontWeight: 700,
                  mb: '8px',
                }}
              >
                Find a Professional
              </GlassCategoryTitle>
              <GlassCategoryDescription
                sx={{
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: { xs: '14px', sm: '16px' },
                  fontWeight: 400,
                  lineHeight: { xs: '143%', sm: '150%' },
                  letterSpacing: { xs: '.17px', sm: '.15px' },
                }}
              >
                Post your task and get offers from qualified professionals
              </GlassCategoryDescription>
            </Box>
          </GlassCategoryCard>
        </Grid>

        <Grid size={{ xs: 6, sm: 6 }}>
          <GlassCategoryCard
            onClick={() => props.onSelect('handyman')}
            sx={{
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'start',
              gap: 2,
              height: '100%',
              transition: 'box-shadow 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              padding: { xs: '22px 12px', sm: '60px 32px' },
              borderRadius: { xs: '32px', sm: '64px' },
            }}
          >
            <GlassCategoryIcon
              sx={{
                width: '128px',
                height: '128px',
              }}
            >
              <HandymanIcon />
            </GlassCategoryIcon>
            <Box sx={{ textAlign: 'center' }}>
              <GlassCategoryTitle
                variant='h6'
                sx={{
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: { xs: '16px', sm: '24px' },
                  lineHeight: { xs: '175%', sm: '133%' },
                  letterSpacing: { xs: '.15px', sm: 0 },
                  fontWeight: 700,
                  mb: '8px',
                }}
              >
                Become a Provider
              </GlassCategoryTitle>
              <GlassCategoryDescription
                sx={{
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: { xs: '14px', sm: '16px' },
                  fontWeight: 400,
                  lineHeight: { xs: '143%', sm: '150%' },
                  letterSpacing: { xs: '.17px', sm: '.15px' },
                }}
              >
                Offer your services, set your own prices, and work on a flexible
                schedule
              </GlassCategoryDescription>
            </Box>
          </GlassCategoryCard>
        </Grid>
      </Grid>
      <RailStepper isProviderFlow={props.isHandyman} activeIndex={0} />
      <ButtonTertiary onClick={() => navigate('/')}>
        Just looking for now
      </ButtonTertiary>
    </Box>
  );
};

export default ChooseRoleStep;
